import { useEffect, useRef } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { GeneralCoachChat } from "@/components/coach-chat-general"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  useLearningEssence,
  useSkipLearningEssence,
  useStartLearningEssence,
} from "@/lib/queries"
import { useCoach } from "@/lib/use-coach"
import { useSessionContext } from "@/lib/session"
import { COACH_FALLBACK_LABEL, initialsOf } from "@nl/shared"

/**
 * The guided onboarding chat, right after the coach was picked.
 *
 * A route rather than a third gate step on purpose: the gate unmounts the
 * moment the coach lands in the session, and the home reminder needs the same
 * screen to resume an open or skipped onboarding later. Skipping is always
 * available — the essence is an offer, not a toll booth.
 */
function OnboardingChatPage() {
  const { role } = useSessionContext()
  const coach = useCoach()
  const name = coach?.name ?? COACH_FALLBACK_LABEL
  const navigate = useNavigate()

  const { data: essence, refetch } = useLearningEssence()
  const start = useStartLearningEssence()
  const skip = useSkipLearningEssence()

  // Completed on arrival means there is nothing to do here. Completing DURING
  // the visit must not yank the page away while the coach is still mirroring
  // the essence back — then the button below changes instead.
  const arrivedCompleted = useRef<boolean | undefined>(undefined)
  if (essence && arrivedCompleted.current === undefined) {
    arrivedCompleted.current = essence.status === "completed"
  }

  useEffect(() => {
    // Admins train like advisors and get the same getting-to-know chat.
    if (role && role !== "advisor" && role !== "platform")
      void navigate({ to: "/", replace: true })
  }, [role, navigate])
  useEffect(() => {
    if (arrivedCompleted.current) void navigate({ to: "/", replace: true })
  }, [essence, navigate])

  // The server creates thread and opening idempotently; the guard only keeps
  // React from firing the request twice while the first one is in flight.
  const startRequested = useRef(false)
  useEffect(() => {
    if (!essence || essence.threadId || startRequested.current) return
    if (essence.status === "completed") return
    startRequested.current = true
    start.mutate({})
  }, [essence, start])

  if (!essence?.threadId || arrivedCompleted.current) return null

  const completed = essence.status === "completed"

  /**
   * "Später" means "let me out" — so a failed request must not leave a dead
   * button. The one case that legitimately fails is an onboarding the coach
   * completed a moment ago, and there the home page is exactly where to go.
   */
  const leave = async () => {
    try {
      await skip.mutateAsync({})
    } catch {
      // Nothing to tell the advisor: they wanted to leave, and they do. The
      // refetch keeps the home page from greeting them with the reminder for an
      // onboarding that is in fact already finished.
      await refetch()
    }
    void navigate({ to: "/" })
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Its own row rather than floating over the thread: an absolutely
          positioned button sat on top of the scrolling messages and, worse,
          under them for the pointer. */}
      <div className="flex shrink-0 justify-end pb-1">
        {completed ? (
          <Button size="sm" onClick={() => void navigate({ to: "/" })}>
            Zur Startseite
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            disabled={skip.isPending}
            onClick={() => void leave()}
          >
            Später
          </Button>
        )}
      </div>
      <GeneralCoachChat
        threadId={essence.threadId}
        mode="onboarding"
        barSize="md"
        autoFocus
        onThreadCreated={() => {}}
        onReset={() => void navigate({ to: "/" })}
        // The completion happens server-side in a tool call; refetching after
        // each answer is what flips "Später" into "Zur Startseite".
        onFinish={() => void refetch()}
        // The thread always carries the persisted opening, so the empty-state
        // hero never renders — this block gives the onboarding the same centered
        // welcome, with the first message flowing right below it.
        aboveThread={
          <div className="reveal flex flex-col items-center px-4 pt-4 text-center">
            <Avatar className="size-20 border border-[var(--rule)]">
              {coach?.portraitUrl ? (
                <AvatarImage src={coach.portraitUrl} alt="" />
              ) : null}
              <AvatarFallback className="bg-accent text-[20px] font-medium text-primary">
                {initialsOf(name)}
              </AvatarFallback>
            </Avatar>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-[16px] font-medium">{name}</span>
              <span className="text-[14px] text-muted-foreground">
                {COACH_FALLBACK_LABEL}
              </span>
            </div>
            <h1 className="mt-4 font-display text-[20px] leading-snug font-medium tracking-tight">
              Kennenlernen mit {name}
            </h1>
            <p className="mt-1.5 max-w-[520px] text-[13.5px] leading-relaxed text-muted-foreground">
              Ein paar Fragen, damit {name} weiß, wo du stehst. Daraus
              entsteht deine Lernentwicklung auf der Startseite.
            </p>
          </div>
        }
        empty={{ title: "Kennenlernen", prompts: [] }}
        placeholder={`Antworte ${name} …`}
      />
    </div>
  )
}

export const Route = createFileRoute("/_app/onboarding")({
  component: OnboardingChatPage,
})
